import {problemsApi as sharedProblemsApi} from '@goproblems/shared/api/problems';
import type {ProblemSolveResponse, SolveProblemRequest} from '@types';
import {uiSlice} from '@reducers/ui';
import {indexPageApi} from '@goproblems/shared/api/indexPage';
import type {RootState} from '../store';

export * from '@goproblems/shared/api/problems';

export const problemsApi = sharedProblemsApi.injectEndpoints({
    endpoints: builder => ({
        solveProblem: builder.mutation<
            ProblemSolveResponse | null,
            SolveProblemRequest
        >({
            invalidatesTags: ['anon_rating', 'training_counter'],
            query: ({
                problemId,
                hardStop,
                path,
                solveTime,
                solveTimeMs,
                solved,
                moveTimes,
                name,
            }) => ({
                url: `/problems/${problemId}/solve`,
                method: 'POST',
                body: {
                    hardStop,
                    path,
                    solveTime,
                    solveTimeMs,
                    solved,
                    moveTimes,
                    name,
                },
            }),
            onQueryStarted: async (
                {hardStop, problemId, solved, isDailyProblem},
                {dispatch, getState, queryFulfilled}
            ) => {
                const updateCachedSoftStopStatus = (
                    hasSoftStopAttempts: boolean
                ) =>
                    dispatch(
                        problemsApi.util.updateQueryData(
                            'fetchProblem',
                            String(problemId),
                            problem => {
                                if (problem.currentUserAttempts) {
                                    problem.currentUserAttempts.hasSoftStopAttempts =
                                        hasSoftStopAttempts;
                                }
                            }
                        )
                    );

                if (!hardStop) {
                    try {
                        await queryFulfilled;
                        updateCachedSoftStopStatus(true);
                    } catch {
                        // A failed request did not create a soft-stop attempt.
                    }
                    return;
                }

                try {
                    const {data} = await queryFulfilled;
                    const solveSucceeded = data?.solved ?? solved;
                    updateCachedSoftStopStatus(false);

                    if (data) {
                        const elo = data.elo_precise ?? data.elo;
                        dispatch(uiSlice.actions.setStatusBarUserElo(elo));
                    }

                    const state = getState() as RootState;
                    const cacheKey = state.auth.user?.id ?? null;
                    let shouldRefreshDailyProblemStatus =
                        isDailyProblem === true;
                    dispatch(
                        indexPageApi.util.updateQueryData(
                            'fetchProblemsOfTheDayStatus',
                            cacheKey,
                            dailyProblemStatus => {
                                const dailyProblem =
                                    dailyProblemStatus.entries.find(
                                        entry => entry.problemId === problemId
                                    );
                                if (!dailyProblem) {
                                    return;
                                }
                                shouldRefreshDailyProblemStatus = true;

                                if (solveSucceeded) {
                                    if (
                                        !dailyProblem.solvedByCurrentUser &&
                                        state.auth.user
                                    ) {
                                        dailyProblem.solversCount += 1;
                                    }
                                    dailyProblem.solvedByCurrentUser = true;
                                } else if (
                                    !dailyProblem.solvedByCurrentUser
                                ) {
                                    dailyProblem.firstSolveAttemptFailedByCurrentUser =
                                        true;
                                }
                            }
                        )
                    );

                    if (shouldRefreshDailyProblemStatus && solveSucceeded) {
                        void dispatch(
                            indexPageApi.endpoints.fetchProblemsOfTheDayStatus.initiate(
                                cacheKey,
                                {subscribe: false, forceRefetch: true}
                            )
                        );
                    }
                } catch {
                    // Keep the cached user and daily problem state unchanged.
                }
            },
        }),
    }),
});

export const {useSolveProblemMutation} = problemsApi;
